import './index.scss';
import { isLang, lang } from '@umu/ukit-lang';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
const { useMemo, useRef, useState } = React;
import { RadioItem } from '@umu/umu-design';
import { UploadLoading } from 'businessCommon/src/components/@uploadLoading/uploadLoading';
import { getUploadResult, IPayloadData } from 'businessCommon/src/services/uploader/uploader';
import { EMediaType } from '@umu/umu-types';
import { ReactCropper } from '../../../reactCropper';
import { components } from 'common';
const { If } = components;
export type { IPayloadData } from 'businessCommon/src/services/uploader/uploader';

interface IProps {
    input: string | File;
    show: boolean;
    teacherId: string;
    className?: string;
    aspect?: number;
    isEdit?: boolean;
    mediaType?: EMediaType;
    onErr?(): void;
    onCancel(): void;
    onConfirm(data?: IPayloadData): void;
}

export const ImageCropEditor = (props: IProps) => {
    const { aspect, input, show, teacherId, isEdit, mediaType = EMediaType.IMAGE, className = '', onErr, onCancel, onConfirm } = props;
    const src = useMemo(() => typeof input === 'string' ? input : window.URL.createObjectURL(input), [input]);
    const originIsFile = typeof input !== 'string';
    const [useOriginImg, setUseOriginImg] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const cropperRef = useRef<HTMLImageElement>(null);
    const [uploading, setUploading] = useState(false);

    const rotateToLeft = () => {
        const imageElement: any = cropperRef?.current;
        const cropper: any = imageElement?.cropper;
        cropper.rotate(-90).zoomTo(0);
        const canvasData = cropper.getCanvasData();
        cropper.setCropBoxData(canvasData);
    };

    const rotateToRight = () => {
        const imageElement: any = cropperRef?.current;
        const cropper: any = imageElement?.cropper;
        cropper.rotate(90).zoomTo(0);
        const canvasData = cropper.getCanvasData();
        // const containerData = cropper.getContainerData();
        // cropper.setCanvasData({maxLeft: containerData.width - canvasData.width, maxTop: containerData.height - canvasData.height});
        cropper.setCropBoxData(canvasData);
    };

    const doUpload = (blob: Blob | null) => {
        if (!blob) {
            onErr && onErr();
            return;
        }
        const previewUrl = window.URL.createObjectURL(blob);
        const param = {
            blob,
            ext: 'png',
            previewUrl,
            fileName: '',
            mediaType,
            teacherId,
        };
        setUploading(true)
        UploadLoading.show({ message: lang('正在上传...') });
        getUploadResult(param)
            .then((payloadData) => {
                onConfirm(payloadData);
            })
            .catch(() => {
                onErr && onErr();
            })
            .finally(() => {
                UploadLoading.hide();
                window.URL.revokeObjectURL(previewUrl);
                setDisabled(false);
                setUploading(false);
            });
    };

    const confirmHandle = () => {
        if (disabled) {
            return;
        }
        if (useOriginImg) {
            originIsFile ? doUpload(input) : onConfirm();
            return;
        }
        setDisabled(true);
        const imageElement: any = cropperRef?.current;
        const cropper: any = imageElement?.cropper;
        cropper.getCroppedCanvas().toBlob(doUpload, 'image/png', 1);
    };

    const CropperEle = useMemo(() => {
        return (
            <ReactCropper
                src={src}
                viewMode={2}
                guides={false}
                rotatable
                movable={true}
                restore={false}
                scalable={false}
                zoomable={true}
                checkOrientation
                ref={cropperRef}
                autoCropArea={1}
                aspectRatio={aspect}
                canvasAlwaysCenter={true}
                toggleDragModeOnDblclick={false}
                style={{ height: '100%', width: '100%' }}
            />
        );
    }, [src]);

    const renderImg = () => {
        const bgOrigin = {
            background: `url(${src}) #000 no-repeat center / contain`,
        };
        return (
            <>
                <If condition={!isEdit && useOriginImg}>
                    <div className="image-crop-editor__preview__origin" style={bgOrigin} />
                </If>
                {CropperEle}
            </>
        );
    };

    if (uploading || !show) {
        return <></>;
    }

    return ReactDOM.createPortal(
        <div className={`cover-page-portal image-crop-portal ${className}`}>
            <div className="cover-page-portal__content image-crop-editor flex--between--center">
                <div className="image-crop-editor__tools flex--between--center">
                    <If condition={!useOriginImg}>
                        <>
                            <button className="image-crop-editor__tools--rotate left" onClick={rotateToLeft}>
                                {lang('左旋 90°')}
                            </button>
                            <button className="image-crop-editor__tools--rotate right" onClick={rotateToRight}>
                                {lang('右旋 90°')}
                            </button>
                        </>
                    </If>
                </div>
                <div className="image-crop-editor__preview flex--1 flex--center">{renderImg()}</div>
                <div className="image-crop-editor__operas flex--between--center">
                    <button onClick={onCancel} className="btn-opera cancel">
                        {lang('取消')}
                    </button>
                    <If condition={!isEdit && !aspect}>
                        <RadioItem<boolean>
                            data={{ label: lang('使用原图'), key: true }}
                            active={useOriginImg}
                            onClick={() => {
                                setUseOriginImg(!useOriginImg);
                            }}
                        />
                    </If>
                    <button onClick={confirmHandle} className={`btn-opera confirm ${disabled ? 'disabled' : ''}`}>
                        {isLang('en') ? 'SAVE' : lang('完成')}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
